import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Modal, Fade, Grid, Backdrop, Typography, Divider, RadioGroup, Radio, FormControlLabel, FormControl } from '@material-ui/core'
import PrintIcon from '@material-ui/icons/Print'
import { CustomButton } from 'components/CustomButton'
import useStyles from './ModalImpressao.styles'
import moment from 'moment'
import { useAuth } from 'hooks/useAuth'
import { QRCodeSVG } from 'qrcode.react'
import clsx from 'clsx'

const DYNAMIC_PAGE_STYLE_ID = 'dynamic-page-style-id'

const getPageCSS = (formato) => {
  switch (formato) {
    case 'L42_LARGA':
      return `@page { size: 108mm; margin: 2mm; }`
    case 'L42_MEDIA':
      return `@page { size: 100mm 150mm; margin: 2mm; }`
    case 'L42_PEQUENA':
      return `@page { size: 70mm 40mm; margin: 0mm; }`
    case 'A4':
    default:
      return `@page { size: A4; margin: 20mm; }`
  }
}

export const ModalImpressao = (props) => {
  const { aberto, setAberto, processando, disabled = false, titulo, children, vestigio } = props
  const { currentUser } = useAuth()
  const classes = useStyles()
  const [formato, setFormato] = useState('A4')

  useEffect(() => {
    const css = getPageCSS(formato)
    const style = document.createElement('style')
    style.id = DYNAMIC_PAGE_STYLE_ID
    style.innerHTML = css
    document.head.appendChild(style)

    return () => {
      const oldStyle = document.getElementById(DYNAMIC_PAGE_STYLE_ID)
      if (oldStyle) oldStyle.remove()
    }
  }, [aberto, formato])

  const fecharModal = () => setAberto(false)
  const aoMudarFormato = (event) => setFormato(event.target.value)
  const executarImpressao = () => window.print()

  const qrCodeUrl = vestigio ? `https://sigep.pci.rn.gov.br/casos/caso/vestigio/detalhar/${vestigio.id}` : ''
  const isEtiquetaPequena = formato === 'L42_PEQUENA'

  const dataExibicao = vestigio ? vestigio.dataColeta || vestigio.createdAt : null
  const dataFormatada = dataExibicao ? moment(dataExibicao).format('DD/MM/YY HH:mm') : ''

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      open={aberto}
      onClose={fecharModal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={aberto}>
        <Grid container className={classes.paper} id="modal-paper">
          {/* Cabeçalho do Modal em Tela (Ocultado na Impressão) */}
          <Grid item xs={12} className={clsx(classes.headerScreen, 'no-print')}>
            <PrintIcon />
            <Typography variant="h6" component="h2" style={{ fontWeight: 600 }}>
              {titulo ? titulo.toUpperCase() : 'IMPRIMIR VESTÍGIO'}
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            id="printable-area"
            className={clsx(classes.printableArea, isEtiquetaPequena && classes.etiquetaPequenaArea)}
          >
            {/* Título Interno (Impresso na etiqueta/documento) */}
            {!isEtiquetaPequena && (
              <Grid style={{ marginBottom: '12px' }}>
                <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#000000' }}>
                  {titulo ? titulo.toUpperCase() : ''}
                </Typography>
                <Divider style={{ margin: '6px 0', backgroundColor: '#000000' }} />
              </Grid>
            )}

            {/* Corpo do Espelho/Etiqueta */}
            <Grid item xs={12}>
              <Grid container>
                {vestigio ? (
                  isEtiquetaPequena ? (
                    // ETIQUETA PEQUENA
                    <Grid container className={classes.etiquetaContainer}>
                      <Grid item xs={8} style={{ paddingRight: '4px' }}>
                        <Typography variant="body2" className={classes.etiquetaTexto}>
                          <strong>Vestígio:</strong> {vestigio.identificador || 'N/A'}
                        </Typography>

                        {vestigio.deExtracaoCadaver && (
                          <Typography variant="body2" className={classes.etiquetaTexto}>
                            <strong>NIC:</strong> {vestigio.cadavOrig?.nic || 'N/A'}
                          </Typography>
                        )}

                        <Typography variant="body2" className={classes.etiquetaTexto}>
                          <strong>Data Coleta:</strong> {dataFormatada}
                        </Typography>

                        <Typography variant="body2" component="div" className={classes.etiquetaDescricao}>
                          <strong>Descrição:</strong> {vestigio.descricao || ''}
                        </Typography>
                        <Typography variant="body2" component="div" className={classes.etiquetaImpressoPor}>
                          {`Impresso por ${currentUser.nome}, ${moment(new Date()).format('DD/MM/YY HH:mm')}`}
                        </Typography>
                      </Grid>

                      <Grid item xs={4} className={classes.qrContainer}>
                        <QRCodeSVG value={qrCodeUrl} size={85} level={'M'} />
                      </Grid>
                    </Grid>
                  ) : (
                    // LAYOUT PADRÃO (A4, Larga, Média)
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={8}>
                            <Grid container spacing={1}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" style={{ color: '#000000' }}>
                                  <strong>Vestígio:</strong>
                                </Typography>
                                <Typography variant="body2" style={{ color: '#000000' }}>
                                  {vestigio.identificador || 'Não especificado'}
                                </Typography>
                              </Grid>
                              {vestigio.deExtracaoCadaver && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" style={{ color: '#000000' }}>
                                    <strong>NIC:</strong>
                                  </Typography>
                                  <Typography variant="body2" style={{ color: '#000000' }}>
                                    {vestigio.cadavOrig?.nic || 'Não especificado'}
                                  </Typography>
                                </Grid>
                              )}
                              <Grid item xs={12}>
                                <Typography variant="body2" style={{ color: '#000000' }}>
                                  <strong>Data e hora da coleta:</strong>
                                </Typography>
                                <Typography variant="body2" style={{ color: '#000000' }}>
                                  {vestigio.dataColeta ? moment(vestigio.dataColeta).format('LLLL') : moment(vestigio.createdAt).format('LLLL')}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body2" style={{ color: '#000000' }}>
                                  <strong>Descrição:</strong>
                                </Typography>
                                <Typography variant="body2" align="justify" style={{ color: '#000000' }}>
                                  {(vestigio.descricao || 'Sem descrição').length > 500 ? `${(vestigio.descricao || 'Sem descrição').substring(0, 500)}...` : vestigio.descricao || 'Sem descrição'}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12} sm={4} style={{ textAlign: 'center', alignSelf: 'center' }}>
                            <QRCodeSVG value={qrCodeUrl} size={110} level={'H'} />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} style={{ marginTop: '8px' }}>
                        <Typography variant="caption" className={classes.etiquetaImpressoPor}>
                          {`Impresso por ${currentUser.nome}, ${moment(new Date()).format('LLLL')}`}
                        </Typography>
                      </Grid>
                    </Grid>
                  )
                ) : (
                  children
                )}
              </Grid>
            </Grid>
          </Grid>

          {/* Área de Botões e Controles */}
          <Grid item xs={12} className="no-print">
            <FormControl component="fieldset" className={classes.formatoContainer} fullWidth>
              <Typography className={classes.formatoTitulo}>Formato de Impressão</Typography>
              <RadioGroup
                row
                aria-label="formato"
                name="formato"
                value={formato}
                onChange={aoMudarFormato}
                style={{ flexWrap: 'wrap' }}
              >
                <FormControlLabel
                  value="A4"
                  control={<Radio color="primary" />}
                  label="A4"
                  className={classes.radioLabel}
                />
                <FormControlLabel
                  value="L42_LARGA"
                  control={<Radio color="primary" />}
                  label="Larga (108mm)"
                  className={classes.radioLabel}
                />
                <FormControlLabel
                  value="L42_MEDIA"
                  control={<Radio color="primary" />}
                  label="Média (100x150)"
                  className={classes.radioLabel}
                />
                <FormControlLabel
                  value="L42_PEQUENA"
                  control={<Radio color="primary" />}
                  label="Pequena (70x40)"
                  className={classes.radioLabel}
                />
              </RadioGroup>
            </FormControl>

            <Divider style={{ marginBottom: '16px' }} />

            <Grid container className={classes.botoesContainer} justifyContent="flex-end" alignItems="center">
              <Grid item xs={12} sm={5}>
                <CustomButton
                  variant="outlined"
                  colorType="primary"
                  onClick={fecharModal}
                  fullWidth
                >
                  Cancelar
                </CustomButton>
              </Grid>

              <Grid item xs={12} sm={5}>
                <CustomButton
                  variant="contained"
                  colorType="primary"
                  disabled={processando || disabled}
                  isLoading={processando}
                  onClick={executarImpressao}
                  fullWidth
                >
                  Imprimir
                </CustomButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Fade>
    </Modal>
  )
}

ModalImpressao.propTypes = {
  aberto: PropTypes.bool.isRequired,
  setAberto: PropTypes.func.isRequired,
  processando: PropTypes.bool,
  disabled: PropTypes.bool,
  titulo: PropTypes.string,
  children: PropTypes.node,
  vestigio: PropTypes.object,
}

